import type { CartographyResponseDto } from '@app/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Background,
  type Connection,
  ConnectionLineType,
  type Edge,
  type Node,
  type OnSelectionChangeParams,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Check, Cog, Expand, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSiteSelectionStore } from '../../progression/store/site-selection.store';
import {
  CartographyInputSidebar,
  CartographyOutputSidebar,
} from '../components/CartographyIsoSidebars';
import { GroupFlowNode } from '../components/GroupFlowNode';
import { ProcessFlowNode } from '../components/ProcessFlowNode';
import {
  bulkPatchStructureLinks,
  fetchCartography,
  putSiteEdges,
} from '../hooks/useProcessCartographyApi';

const nodeTypes = { groupNode: GroupFlowNode, processNode: ProcessFlowNode };

type NodeData = CartographyResponseDto['nodes'][number]['data'] & {
  isEditMode?: boolean;
  layoutW: number;
  layoutH: number;
};

function toRfNodes(dto: CartographyResponseDto, isEditMode: boolean): Node[] {
  return dto.nodes.map((n) => {
    const parentId = n.parentId ?? undefined;
    const base: Node = {
      id: n.id,
      type: n.type,
      position: n.position,
      parentId,
      style: { width: n.width, height: n.height },
      data: {
        ...(n.data as NodeData),
        isEditMode,
        layoutW: n.width,
        layoutH: n.height,
      },
      draggable: isEditMode,
    };
    if (n.type === 'processNode' && parentId) {
      base.extent = 'parent';
    }
    return base;
  });
}

function toRfEdges(dto: CartographyResponseDto, isEditMode: boolean): Edge[] {
  return dto.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle ?? undefined,
    targetHandle: e.targetHandle ?? undefined,
    type: e.type ?? 'smoothstep',
    animated: e.animated ?? false,
    selectable: isEditMode,
    style: { stroke: '#0ea5e9', strokeWidth: 2 },
  }));
}

function parseStyleDim(value: number | string | undefined): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Après NodeResizer / mesure RF, la taille peut être dans width/height, measured ou style. */
function readNodeLayout(n: Node) {
  const data = n.data as NodeData;
  const w = n.width ?? n.measured?.width ?? parseStyleDim(n.style?.width) ?? data.layoutW ?? 160;
  const h = n.height ?? n.measured?.height ?? parseStyleDim(n.style?.height) ?? data.layoutH ?? 80;
  return {
    structureLinkId: n.id,
    positionX: Math.round(n.position.x),
    positionY: Math.round(n.position.y),
    width: Math.round(w),
    height: Math.round(h),
  };
}

type FlowChromeProps = {
  isEditMode: boolean;
  onToggleEditMode: () => void;
  isLoading: boolean;
  groupCount: number;
  processCount: number;
  selectedEdgeCount: number;
  onDeleteSelectedEdges: () => void;
  onSave: () => void;
  savePending: boolean;
};

/** Boutons Fit View / Éditer / Valider — doit rester sous `<ReactFlow>`. */
function CartographyFlowChrome({
  isEditMode,
  onToggleEditMode,
  isLoading,
  groupCount,
  processCount,
  selectedEdgeCount,
  onDeleteSelectedEdges,
  onSave,
  savePending,
}: FlowChromeProps) {
  const { fitView } = useReactFlow();

  return (
    <>
      <Background color="#cbd5e1" gap={20} size={2} />
      <Panel position="top-right" className="m-2 flex flex-wrap items-center justify-end gap-2">
        {isEditMode && selectedEdgeCount > 0 ? (
          <span className="rounded-md bg-amber-100 px-2 py-1 font-semibold text-amber-950 text-[0.7rem]">
            {selectedEdgeCount} connexion{selectedEdgeCount > 1 ? 's' : ''} sélectionnée
            {selectedEdgeCount > 1 ? 's' : ''}
          </span>
        ) : null}
        {isEditMode && selectedEdgeCount > 0 ? (
          <button
            type="button"
            onClick={onDeleteSelectedEdges}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 font-semibold text-white text-xs shadow-sm hover:bg-red-700"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            Supprimer
          </button>
        ) : null}
        <button
          type="button"
          onClick={() =>
            void fitView({
              padding: 0.12,
              duration: 500,
              maxZoom: 1.8,
              minZoom: 0.2,
            })
          }
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 font-semibold text-white text-xs shadow-sm hover:bg-emerald-700"
          title="Recadrer la vue"
        >
          <Expand className="h-3.5 w-3.5" aria-hidden />
          Fit View
        </button>
        {isEditMode ? (
          <button
            type="button"
            disabled={savePending}
            onClick={() => onSave()}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 font-semibold text-white text-xs shadow-sm hover:bg-emerald-700 disabled:opacity-60"
          >
            {savePending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Check className="h-3.5 w-3.5" aria-hidden />
            )}
            {savePending ? 'Sauvegarde…' : 'Valider'}
          </button>
        ) : null}
        <button
          type="button"
          disabled={isLoading}
          onClick={onToggleEditMode}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-semibold text-white text-xs shadow-sm disabled:opacity-60 ${
            isEditMode ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : isEditMode ? (
            <X className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          )}
          {isLoading ? 'Chargement…' : isEditMode ? 'Annuler' : 'Éditer'}
        </button>
      </Panel>
      <Panel position="bottom-left" className="m-2 max-w-xs">
        <p className="rounded-md bg-white/95 px-2 py-1 text-[0.65rem] text-slate-500 shadow-sm">
          {isEditMode
            ? 'Mode édition : déplacez les blocs, reliez les processus (poignées verte / bleue), validez pour enregistrer.'
            : 'Mode lecture : parcouriz la carte. Activez « Éditer » pour modifier.'}
        </p>
      </Panel>
      <FlowFitOnCount processCount={processCount} groupCount={groupCount} />
    </>
  );
}

function FlowFitOnCount({
  processCount,
  groupCount,
}: { processCount: number; groupCount: number }) {
  const { fitView } = useReactFlow();
  const n = processCount + groupCount;
  const done = useRef(false);
  useEffect(() => {
    if (n === 0) {
      done.current = false;
      return;
    }
    if (done.current) return;
    done.current = true;
    const t = window.setTimeout(() => {
      void fitView({ padding: 0.14, duration: 600, maxZoom: 1.6, minZoom: 0.25 });
    }, 150);
    return () => window.clearTimeout(t);
  }, [n, fitView]);
  return null;
}

export function CartographyPage() {
  return (
    <ReactFlowProvider>
      <CartographyPageContent />
    </ReactFlowProvider>
  );
}

function CartographyPageContent() {
  const siteId = useSiteSelectionStore((s) => s.selectedSiteId);
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEdges, setSelectedEdges] = useState<Edge[]>([]);

  const cartographyQuery = useQuery({
    queryKey: ['cartography', siteId],
    queryFn: () => {
      if (siteId == null || siteId === '') {
        return Promise.reject(new Error('Site requis'));
      }
      return fetchCartography(siteId);
    },
    enabled: Boolean(siteId),
  });

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  useEffect(() => {
    if (!cartographyQuery.data) return;
    setNodes(toRfNodes(cartographyQuery.data, isEditMode));
    setEdges(toRfEdges(cartographyQuery.data, isEditMode));
    setSelectedEdges([]);
  }, [cartographyQuery.data, isEditMode, setNodes, setEdges]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!siteId) throw new Error('Site requis');
      await bulkPatchStructureLinks(siteId, { updates: nodes.map(readNodeLayout) });

      const linkToProcess = new Map<string, string>();
      for (const n of nodes) {
        if (n.type === 'processNode') {
          const pid = (n.data as NodeData).processId;
          if (pid) linkToProcess.set(n.id, pid);
        }
      }
      const edgePayload = edges
        .map((e) => ({
          sourceProcessId: linkToProcess.get(e.source),
          targetProcessId: linkToProcess.get(e.target),
        }))
        .filter((x): x is { sourceProcessId: string; targetProcessId: string } =>
          Boolean(x.sourceProcessId && x.targetProcessId),
        );
      await putSiteEdges(siteId, { edges: edgePayload });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cartography', siteId] });
      setIsEditMode(false);
    },
  });

  const onConnect = useCallback(
    (params: Connection) => {
      if (!isEditMode) return;
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: ConnectionLineType.SmoothStep,
            animated: false,
            style: { stroke: '#0ea5e9', strokeWidth: 2 },
          },
          eds,
        ),
      );
    },
    [isEditMode, setEdges],
  );

  const onSelectionChange = useCallback(({ edges: selEdges }: OnSelectionChangeParams) => {
    setSelectedEdges(selEdges ?? []);
  }, []);

  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => {
      if (prev) {
        void queryClient.invalidateQueries({ queryKey: ['cartography', siteId] });
      }
      return !prev;
    });
  }, [queryClient, siteId]);

  const deleteSelectedEdges = useCallback(() => {
    if (selectedEdges.length === 0) return;
    const ids = new Set(selectedEdges.map((e) => e.id));
    setEdges((eds) => eds.filter((e) => !ids.has(e.id)));
    setSelectedEdges([]);
  }, [selectedEdges, setEdges]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isEditMode || (e.key !== 'Delete' && e.key !== 'Backspace')) return;
      if (selectedEdges.length === 0) return;
      e.preventDefault();
      deleteSelectedEdges();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isEditMode, selectedEdges.length, deleteSelectedEdges]);

  const groupCount = useMemo(() => nodes.filter((n) => n.type === 'groupNode').length, [nodes]);
  const processCount = useMemo(() => nodes.filter((n) => n.type === 'processNode').length, [nodes]);
  const edgeCount = edges.length;

  if (!siteId) {
    return <p className="p-6 text-slate-600">Sélectionnez un site dans l’en-tête.</p>;
  }

  const loading = cartographyQuery.isLoading;

  return (
    <div className="flex min-h-0 w-full flex-1 gap-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <CartographyInputSidebar />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-slate-200 border-b bg-slate-100 px-4 py-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <Cog className="h-6 w-6 shrink-0 text-sky-600" aria-hidden />
            <h2 className="font-bold text-base text-slate-900">Processus Métiers</h2>
            <div className="flex items-center gap-1.5 text-emerald-600 text-xs">
              <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
              <span className="font-semibold">
                {groupCount} groupe{groupCount > 1 ? 's' : ''}, {processCount} processus
                {processCount > 1 ? '' : ''}, {edgeCount} connexion{edgeCount > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col bg-slate-100">
          {cartographyQuery.isError ? (
            <div className="flex items-center justify-center p-6 text-center text-red-600 text-sm">
              Impossible de charger la cartographie.
            </div>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onSelectionChange={onSelectionChange}
              nodesDraggable={isEditMode}
              nodesConnectable={isEditMode}
              elementsSelectable={isEditMode}
              nodeTypes={nodeTypes}
              connectionLineType={ConnectionLineType.SmoothStep}
              minZoom={0.2}
              maxZoom={1.8}
              snapToGrid={isEditMode}
              snapGrid={[20, 20]}
              panOnScroll
              zoomOnScroll
              zoomOnPinch
              className="h-full min-h-0 w-full flex-1 touch-manipulation"
              defaultEdgeOptions={{
                zIndex: 1000,
                selectable: isEditMode,
                style: { stroke: '#0ea5e9', strokeWidth: 2 },
              }}
              proOptions={{ hideAttribution: true }}
              isValidConnection={(c) => c.source !== c.target}
            >
              <CartographyFlowChrome
                isEditMode={isEditMode}
                onToggleEditMode={toggleEditMode}
                isLoading={loading}
                groupCount={groupCount}
                processCount={processCount}
                selectedEdgeCount={selectedEdges.length}
                onDeleteSelectedEdges={deleteSelectedEdges}
                onSave={() => saveMut.mutate()}
                savePending={saveMut.isPending}
              />
            </ReactFlow>
          )}
        </div>
      </div>

      <CartographyOutputSidebar />
    </div>
  );
}
